import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { orderLineApi } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import OrderLineCard from '../components/OrderLineCard';
import StatusFilterDropdown from '../components/StatusFilterDropdown';
import LoadingScreen from '../components/LoadingScreen';
import { FunnelIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface OrderLineData {
  sessionCode: string;
  sessionId: number;
  status: string;
  startedAt: string;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  qrCode: string;
  location: string;
  customerIdentifier: string;
  customerType: string;
  orderCount: number;
  orders: Array<{
    id: number;
    customOrderId: string;
    status: string;
    total: number;
    createdAt: string;
    items: Array<{
      name: string;
      quantity: number;
      price: number;
    }>;
  }>;
}

/** Cards painted at once. Each OrderLineCard is ~36 DOM nodes, so an
 *  unbounded list turns a busy vendor's history into six figures of nodes. */
const ORDER_LINE_RENDER_LIMIT = 50;

const VendorOrderLines: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [orderLines, setOrderLines] = useState<OrderLineData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch OrderLines
  const fetchOrderLines = useCallback(async () => {
    if (!user) {
      setError("User not authenticated.");
      setLoading(false);
      return;
    }

    try {
      // Fetch all sessions without status filter - filtering will be done on frontend
      const response = await orderLineApi.getVendorSessions();
      const data = response.data;
      
      // Sort by startedAt descending
      const sortedData = data.sort((a: OrderLineData, b: OrderLineData) => 
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
      );
      
      setOrderLines(sortedData);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching order lines:', error);
      setError(error.response?.data?.message || 'Failed to fetch order lines');
      toast.error('Failed to fetch order lines');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [user]);


  // Initial load
  useEffect(() => {
    fetchOrderLines();
  }, [fetchOrderLines]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchOrderLines();
  };

  const handleOrderClick = (orderId: number) => {
    // Navigate to the specific order detail page
    navigate(`/vendor/orders/${orderId}`);
  };

  const handleStatusUpdate = async (sessionId: number, newStatus: string) => {
    try {
      await orderLineApi.updateOrderLineStatus(sessionId, newStatus);
      toast.success(`OrderLine status updated to ${newStatus}`);
      fetchOrderLines(); // Refresh the list
    } catch (error: any) {
      console.error('Error updating OrderLine status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  // Filter orderLines based on status
  const filteredOrderLines = orderLines.filter(orderLine => {
    if (statusFilter === 'ALL') return true;
    return orderLine.status === statusFilter;
  });

  const statusOptions = [
    { value: 'ALL', label: 'All Sessions' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'BILL_REQUESTED', label: 'Bill Requested' },
    { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
    { value: 'PAID', label: 'Paid' },
    { value: 'CLOSED', label: 'Closed' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="mb-4" style={{ color: theme.danger }}>{error}</p>
          <button
            onClick={fetchOrderLines}
            className="px-4 py-2 rounded-lg transition-colors"
            style={{ backgroundColor: theme.button?.primary?.bg || theme.primary, color: theme.button?.primary?.text || '#FFFFFF' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-3 py-4 sm:p-4" style={{ backgroundColor: theme.background }}>
      {/* Toaster provided globally via ToastProvider */}
      
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-4">
          <h1 className="text-xl sm:text-2xl font-bold leading-tight" style={{ color: theme.text }}>
            Table Order Sessions
          </h1>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="self-end sm:self-auto p-2 rounded-lg transition-colors"
            style={{
              backgroundColor: isRefreshing ? theme.gray[200] : (theme.components?.card?.background || '#FFFFFF'),
              border: isRefreshing ? 'none' : `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
            }}
          >
            <ArrowPathIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} style={{ color: theme.gray[500] }} />
          </button>
        </div>

        {/* Filter */}
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="w-5 h-5 flex-shrink-0" style={{ color: theme.gray[500] }} />
            <StatusFilterDropdown
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
            />
          </div>
          <span className="text-sm text-center sm:text-left" style={{ color: theme.gray[500] }}>
            {filteredOrderLines.length} session{filteredOrderLines.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* OrderLine Cards */}
      {filteredOrderLines.length === 0 ? (
        <div className="text-center py-12">
          <p style={{ color: theme.gray[500] }}>
            No order sessions found
          </p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {/* Capped render. fetchOrderLines pulls EVERY session ever (see its
              "Fetch all sessions without status filter" comment) and this list
              drew one card per session — measured 3,987 cards / 143,482 DOM
              nodes, a 1,148,268px-tall page on a phone. That is unusable on a
              till and made the page impossible to screenshot. The count above
              still reports the true total; the status filter still narrows the
              whole set, not just the visible slice.
              TODO: proper server-side paging / date scoping — the fetch itself
              is still unbounded, this only bounds what we paint. */}
          {filteredOrderLines.slice(0, ORDER_LINE_RENDER_LIMIT).map((orderLine) => (
            <OrderLineCard
              key={orderLine.sessionCode}
              orderLine={orderLine}
              onOrderClick={handleOrderClick}
              onStatusUpdate={handleStatusUpdate}
            />
          ))}
          {filteredOrderLines.length > ORDER_LINE_RENDER_LIMIT && (
            <p className="text-sm text-center py-4" style={{ color: theme.gray[500] }}>
              Showing the {ORDER_LINE_RENDER_LIMIT} most recent of {filteredOrderLines.length} sessions.
              Use the status filter to narrow the list.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorOrderLines;